import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
let stripePromise = null;
const getStripe = async () => {
  if (!stripePromise) {
    stripePromise = axios.get(`${API}/payments/config`).then((r) => loadStripe(r.data.publishable_key));
  }
  return stripePromise;
};

const CheckoutForm = ({ intentId, amountLabel, onPaid }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [ready, setReady] = useState(false);

  const pay = async () => {
    if (!stripe || !elements) return;
    setSubmitting(true);
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: `${window.location.origin}/payment/success?session_id=${intentId}` },
      redirect: 'if_required',
    });
    if (error) {
      toast.error(error.message || 'El pago no se pudo completar');
      setSubmitting(false);
      return;
    }
    if (paymentIntent && (paymentIntent.status === 'succeeded' || paymentIntent.status === 'processing')) {
      onPaid(paymentIntent);
    } else {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="stripe-payment-element-wrapper">
      {/* Payment methods come from the PaymentIntent (Dashboard config), never from a static list */}
      <PaymentElement onReady={() => setReady(true)} options={{ layout: 'tabs' }} />
      <Button onClick={pay} disabled={!ready || submitting} data-testid="paywall-pay-button" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-base font-medium">
        {submitting ? 'Procesando pago…' : `Pagar ${amountLabel}`}
      </Button>
    </div>
  );
};

// Embedded Stripe checkout: creates a PaymentIntent with automatic_payment_methods
// and renders the PaymentElement bound to its client_secret.
const StripeCheckout = ({ plan, amountLabel, onPaid, onError }) => {
  const [stripe, setStripe] = useState(null);
  const [intent, setIntent] = useState(null);
  const intentReq = useRef({ plan: null, promise: null }); // one PaymentIntent per plan, even under StrictMode double-mount

  useEffect(() => {
    let cancelled = false;
    if (intentReq.current.plan !== plan) {
      const token = localStorage.getItem('token');
      intentReq.current = { plan, promise: axios.post(`${API}/payments/intent`, { plan }, { headers: { Authorization: `Bearer ${token}` } }) };
    }
    (async () => {
      try {
        const [s, r] = await Promise.all([getStripe(), intentReq.current.promise]);
        if (!cancelled) { setStripe(s); setIntent(r.data); }
      } catch (err) {
        intentReq.current = { plan: null, promise: null };
        if (!cancelled) onError?.(err.response?.data?.detail || 'No se pudo iniciar el pago');
      }
    })();
    return () => { cancelled = true; };
  }, [plan]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!stripe || !intent) {
    return <p className="text-sm text-slate-500 text-center py-6" data-testid="stripe-loading">Cargando métodos de pago…</p>;
  }
  return (
    <Elements
      stripe={stripe}
      options={{ clientSecret: intent.client_secret, locale: 'auto', appearance: { theme: 'stripe', variables: { colorPrimary: '#059669', borderRadius: '8px' } } }}
    >
      <CheckoutForm intentId={intent.intent_id} amountLabel={amountLabel} onPaid={(pi) => onPaid(intent.intent_id, pi)} />
    </Elements>
  );
};

export default StripeCheckout;
